import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { convertToBase64 } from '../utils/imageUtils';

/**
 * واجهة إعدادات ImgBB
 */
export interface ImgBBSettings {
  apiKey: string;
  enabled: boolean;
}

// الإعدادات الافتراضية
const DEFAULT_IMGBB_SETTINGS: ImgBBSettings = {
  apiKey: '',
  enabled: false
};

/**
 * الحصول على إعدادات ImgBB
 * @returns وعد بإعدادات ImgBB
 */
export const getImgBBSettings = async (): Promise<ImgBBSettings> => {
  try {
    // محاولة الحصول على الإعدادات من Firestore
    const settingsDoc = await getDoc(doc(db, 'settings', 'imgbb'));

    if (settingsDoc.exists()) {
      const data = settingsDoc.data() as ImgBBSettings;
      return {
        apiKey: data.apiKey || '',
        enabled: data.enabled || false
      };
    }

    // إذا لم تكن الإعدادات موجودة، استخدم الإعدادات الافتراضية
    return DEFAULT_IMGBB_SETTINGS;
  } catch (error) {
    console.error('Error getting ImgBB settings:', error);
    return DEFAULT_IMGBB_SETTINGS;
  }
};

/**
 * حفظ إعدادات ImgBB
 * @param settings إعدادات ImgBB
 * @returns وعد بنتيجة عملية الحفظ
 */
export const saveImgBBSettings = async (settings: ImgBBSettings): Promise<void> => {
  try {
    await setDoc(doc(db, 'settings', 'imgbb'), {
      ...settings,
      updatedAt: new Date()
    });

    console.log('ImgBB settings saved successfully');
  } catch (error) {
    console.error('Error saving ImgBB settings:', error);
    throw error;
  }
};

/**
 * تحميل صورة إلى ImgBB
 * @param file ملف الصورة المراد تحميله
 * @returns وعد بعنوان URL الصورة
 */
export const uploadImageToImgBB = async (file: File): Promise<string> => {
  try {
    // الحصول على إعدادات ImgBB
    const settings = await getImgBBSettings();
    console.log('تم الحصول على إعدادات ImgBB:', JSON.stringify({
      enabled: settings.enabled,
      apiKey: settings.apiKey ? 'موجود' : 'غير موجود'
    }));

    // التحقق من وجود مفتاح API وتفعيل الخدمة
    if (!settings.apiKey || !settings.enabled) {
      console.error('إعدادات ImgBB غير مكتملة أو الخدمة غير مفعلة');
      throw new Error('إعدادات ImgBB غير مكتملة أو الخدمة غير مفعلة');
    }

    console.log('تحميل الصورة إلى ImgBB...', file.name, file.size);

    // تحويل الصورة إلى Base64 ثم استخراج بيانات Base64 فقط
    const base64Image = await convertToBase64(file);
    const base64Data = base64Image.split(',')[1]; // استخراج بيانات Base64 فقط (بعد الفاصلة)

    if (!base64Data) {
      console.error('فشل تحويل الصورة إلى Base64');
      throw new Error('فشل تحويل الصورة إلى Base64');
    }

    console.log('تم تحويل الصورة إلى Base64 بنجاح، جاري الإرسال إلى ImgBB...');

    // إنشاء نموذج بيانات لإرسال الصورة
    const formData = new FormData();
    formData.append('key', settings.apiKey);
    formData.append('image', base64Data);

    // إرسال الطلب إلى ImgBB API
    console.log('إرسال الطلب إلى ImgBB API...');
    const response = await fetch('https://api.imgbb.com/1/upload', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      console.error('فشل طلب ImgBB، الحالة:', response.status, response.statusText);
      const errorText = await response.text();
      console.error('نص الخطأ:', errorText);
      throw new Error(`فشل تحميل الصورة إلى ImgBB: ${response.status} ${response.statusText}`);
    }

    // تحليل الاستجابة
    const responseData = await response.json();
    console.log('استجابة ImgBB ناجحة:', JSON.stringify(responseData));

    if (!responseData.data || !responseData.data.url) {
      console.error('لم يتم العثور على رابط الصورة في استجابة ImgBB');
      throw new Error('لم يتم العثور على رابط الصورة في استجابة ImgBB');
    }

    const imageUrl = responseData.data.url;
    console.log('تم تحميل الصورة بنجاح إلى ImgBB:', imageUrl);
    return imageUrl;
  } catch (error) {
    console.error('خطأ في تحميل الصورة إلى ImgBB:', error);
    throw error; // إعادة رمي الخطأ للتعامل معه في المستوى الأعلى
  }
};

/**
 * تحميل مجموعة صور إلى ImgBB
 * @param files مصفوفة ملفات الصور المراد تحميلها
 * @returns وعد بمصفوفة عناوين URL للصور
 */
export const uploadMultipleImagesToImgBB = async (files: File[]): Promise<string[]> => {
  try {
    // إنشاء مصفوفة وعود لتحميل الصور
    const uploadPromises = files.map(file => uploadImageToImgBB(file));

    // انتظار اكتمال تحميل جميع الصور
    const uploadedUrls = await Promise.all(uploadPromises);

    return uploadedUrls;
  } catch (error) {
    console.error('Error uploading multiple images to ImgBB:', error);
    throw error;
  }
};

export default {
  getImgBBSettings,
  saveImgBBSettings,
  uploadImageToImgBB,
  uploadMultipleImagesToImgBB
};
